import type { FC } from 'react';

import { NavLink, useNavigate } from 'react-router-dom';

import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';

import { Outlet } from 'react-router-dom';
import type * as React from 'react';
import { useAuth } from '../Contexts/AuthProvider';
import { NavProfile, NavProfileTheme } from './Components/NavProfile';
import { useTemplate } from './Hooks/useTemplate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faChartPie,
  faClock,
  faFile,
  faList,
  faRocket,
  faSearch,
  faTasks,
} from '@fortawesome/free-solid-svg-icons';
import { AssetURLType, getAssetURL } from '../Utilities/AssetURL';
import { Button, Form, Image, InputGroup } from 'react-bootstrap';
import { t } from 'i18next';
import { useProfileSearch } from '@app/Contexts/ProfileSearchProvider';
import { Search } from './Components/Search';

export const SITE_LOGO = process.env.SITE_LOGO as string;

export const DashboardTemplate = () => {
  useTemplate('dashboard');

  const { openProfileSearch } = useProfileSearch();

  const session = useAuth();

  const DEFAULT_LOGO_WIDTH = 225;

  // Get logos
  const agencyLogo = session.getAgency()?.getAgencyLogo?.() ?? null;
  const userLogo = session.getUser()?.getUserCompanyLogo?.() ?? null;
  const headerBackgroundColor =
    agencyLogo && agencyLogo.getLogoHeaderColor() ? agencyLogo.getLogoHeaderColor(true) : null;

  const agencyLogoWidth = agencyLogo ? agencyLogo.getLogoWidth() : null;
  const agencyLogoMarginLeft = agencyLogo ? agencyLogo.getLogoMarginLeft() : null;
  const agencyLogoMarginTop = agencyLogo ? agencyLogo.getLogoMarginTop() : null;

  const agencyLogoKey = agencyLogo ? agencyLogo.getLogoName() : null;
  const userLogoKey = userLogo ? userLogo.getCompanyLogoName() : null;

  // Top Bar properties
  const topClassList = ['d-flex', 'w-100', 'justify-content-between', 'align-items-center', 'px-4'];
  const topStyle: React.CSSProperties = {};

  if (!headerBackgroundColor) {
    topClassList.push('bg-dark');
    topStyle.backgroundColor = headerBackgroundColor;
  }

  const topProperties = {
    className: topClassList.join(' '),
    style: topStyle,
  };

  // Logo properties
  const logo = getAssetURL(agencyLogoKey ?? userLogoKey ?? AssetURLType.Logos, SITE_LOGO);
  const logoWidth = agencyLogoWidth ?? DEFAULT_LOGO_WIDTH;
  const logoStyle: React.CSSProperties = {};

  if (agencyLogoMarginLeft) logoStyle.marginLeft = agencyLogoMarginLeft;
  if (agencyLogoMarginTop) logoStyle.marginTop = agencyLogoMarginTop;

  return (
    <>
      <Navbar className={`bg-white flex-column align-items-stretch p-0`}>
        {/* Row 1: Brand + Search + Profile + Toggle */}
        <div {...topProperties}>
          {/* Left side: logo + search */}
          <div className="d-flex align-items-center">
            <Navbar.Brand className="my-3" as={NavLink} to="/">
              <Image src={logo} width={logoWidth} style={logoStyle} />
            </Navbar.Brand>

            <Form className="ms-3 d-flex align-items-center" /* onSubmit={handleSearchSubmit} */>
              <InputGroup size="sm" className="ms-3">
                <Search className="shadow-none border-end-0" placeholder={t('nav.search')} />
                <InputGroup.Text className="border-start-0 bg-body">
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                {/* Advanced Search prompt */}
                <Button
                  type="button"
                  variant="primary"
                  className="text-nowrap"
                  onClick={openProfileSearch}
                >
                  {t('nav.advancedSearch')}
                </Button>
              </InputGroup>
            </Form>
          </div>

          {/* Right side: profile + toggle */}
          <div className="d-flex align-items-center">
            {session.isAuthenticated() === true && (
              <NavProfile theme={NavProfileTheme.Dark} size="md" session={session} />
            )}

            <Navbar.Toggle aria-controls="dashboard-navbar-nav" className="ms-2" />
          </div>
        </div>

        {/* Row 2: Collapsed/expanded nav */}
        <Navbar.Collapse id="dashboard-navbar-nav" className="w-100 py-2 px-2">
          <Nav className="me-auto">
            <Nav.Link as={NavLink} to="/agent" className="px-3">
              <FontAwesomeIcon icon={faRocket} /> {t('nav.dashboard')}
            </Nav.Link>
            <Nav.Link as={NavLink} to="/agent/pending" className="px-3 ms-1">
              <FontAwesomeIcon icon={faClock} /> {t('nav.pending')}{' '}
              <small className="badge bg-success mt-n1">148</small>
            </Nav.Link>
            <Nav.Link as={NavLink} to="/agent/leads" className="px-3 ms-1">
              <FontAwesomeIcon icon={faBullseye} /> {t('nav.leads')}
            </Nav.Link>
            <Nav.Link as={NavLink} to="/agent/activities" className="px-3 ms-1">
              <FontAwesomeIcon icon={faTasks} /> {t('nav.activities')}
            </Nav.Link>
            <Nav.Link as={NavLink} to="/agent/documents" className="px-3 ms-1">
              <FontAwesomeIcon icon={faFile} /> {t('nav.docs')}
            </Nav.Link>
            <Nav.Link as={NavLink} to="/agent/my-book" className="px-3 ms-1">
              <FontAwesomeIcon icon={faList} /> {t('nav.reporting')}
            </Nav.Link>
            <Nav.Link as={NavLink} to="/agent/analytics" className="px-3 ms-1">
              <FontAwesomeIcon icon={faChartPie} /> {t('nav.analytics')}
            </Nav.Link>
          </Nav>
        </Navbar.Collapse>
      </Navbar>
      <Outlet />
    </>
  );
};
